import { useAlertUiTranslation } from '../../../../../../ui/hooks';
import { CircleBar } from '@juvare/common-web-ui/CircleBar';
import { showConfirmDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showAlertSnackbar, showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import CachedIcon from '@mui/icons-material/Cached';
import CancelScheduleSendOutlinedIcon from '@mui/icons-material/CancelScheduleSendOutlined';
import { Alert, Box, Button, Typography, useMediaQuery } from '@mui/material';
import { parseISO } from 'date-fns';
import React from 'react';
import { useAlertServiceClient } from '../../../hooks';
import { useAlertRespondersTable } from '../hooks/useAlertRespondersTable';
import { AlertRespondersTable } from './AlertRespondersTable';
export const AlertDeliveryAndResponse = ({ data, onReload, isTabSelected }) => {
    var _a, _b, _c, _d;
    const { t } = useAlertUiTranslation();
    const upSmScreen = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const sentCount = (_a = data.progress.sentCount) !== null && _a !== void 0 ? _a : 0;
    const respondedCount = (_b = data.progress.respondedCount) !== null && _b !== void 0 ? _b : 0;
    const totalCount = data.responderCounts.totalCount;
    const contacted = totalCount > 0 ? (sentCount + respondedCount) / totalCount : 0;
    const responded = totalCount > 0 ? respondedCount / totalCount : 0;
    const result = useAlertRespondersTable(data);
    const alertServiceClient = useAlertServiceClient();
    const allowCancelNotification = !data.terminatedAt && (data.status === 'sending' || data.status === 'failed') && !!data.sendDateTime;
    const boxRef = React.useRef();
    React.useEffect(() => {
        if (boxRef.current && isTabSelected) {
            boxRef.current.focus();
        }
    }, [isTabSelected]);
    const handleCancelNotification = () => {
        showConfirmDialog({
            title: t('terminateAlertDialog.title'),
            content: React.createElement(Typography, null, t('terminateAlertDialog.message')),
            confirmLabel: t('terminateAlertDialog.confirm'),
            cancelLabel: t('terminateAlertDialog.cancel'),
            onConfirm: () => {
                showProgressDialog({
                    title: t('terminateAlertDialog.progress'),
                    callback: (done) => {
                        alertServiceClient
                            .terminateAlert(data.id)
                            .then(() => {
                            onReload();
                            showAlertSnackbar({
                                message: t('pages.tables.details.stopNotificationSuccess'),
                                type: 'success',
                            });
                        })
                            .catch((err) => {
                            showAlertSnackbar({
                                message: err && err.status === 400
                                    ? t('pages.tables.details.stopNotificationError')
                                    : t('pages.tables.details.stopNotificationFail'),
                                type: 'error',
                            });
                            console.error(err);
                        })
                            .finally(() => done());
                    },
                });
            },
        });
    };
    return (React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: 'auto', gap: 2 }, ref: boxRef, tabIndex: -1 },
        React.createElement(Box, { sx: upSmScreen
                ? { display: 'flex', alignItems: 'center', gap: 2 }
                : { display: 'grid', gridTemplateColumns: 'auto', gap: 2 } },
            React.createElement(Typography, { variant: 'subtitle2', sx: { flexGrow: { xs: 0, sm: 1 } } }, t('common.summary')),
            React.createElement(Button, { color: 'primary', variant: 'outlined', startIcon: React.createElement(CachedIcon, null), sx: { width: { xs: '100%', sm: 'auto' } }, onClick: () => {
                    onReload();
                    result.reload();
                    showInfoSnackbar({ message: t('common.refreshed'), autoHideDuration: 2000 });
                } }, t('common.refresh')),
            React.createElement(Button, { color: 'primary', variant: 'outlined', startIcon: React.createElement(CancelScheduleSendOutlinedIcon, null), disabled: !allowCancelNotification, sx: { width: { xs: '100%', sm: 'auto' } }, onClick: handleCancelNotification }, t('common.cancelNotification'))),
        data.status === 'failed' && !data.terminatedAt && (React.createElement(Alert, { severity: 'error' }, t('common.deliveryFailedFullMessage', {
            dateTime: formatDateTime(parseISO((_c = data.sendDateTime) !== null && _c !== void 0 ? _c : data.updated)),
        }))),
        data.terminatedAt && (React.createElement(Alert, { severity: 'warning' }, t('common.deliveryCanceledFullMessage', {
            userName: data.terminatedBy,
            dateTime: formatDateTime(parseISO(data.terminatedAt)),
        }))),
        React.createElement(Box, { sx: {
                display: 'grid',
                gridTemplateColumns: (theme) => upSmScreen
                    ? data.responseOptions.length > 0
                        ? { xs: `repeat(3, calc(33.333% - ${theme.spacing(4 / 3)}))` }
                        : { xs: `repeat(2, calc(50% - ${theme.spacing(1)}))` }
                    : {},
                gap: 2,
            } },
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    gap: 2,
                    backgroundColor: themeColors.surface.secondary,
                } },
                React.createElement(Typography, { variant: 'subtitle1', sx: { width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, (_d = data.responderCounts) === null || _d === void 0 ? void 0 : _d.totalCount),
                React.createElement(Typography, { variant: 'subtitle2' }, t('common.total'))),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    gap: 2,
                    backgroundColor: themeColors.surface.secondary,
                } },
                React.createElement(CircleBar, { size: 64, strokeWidth: 5, antiClockwise: true, value: contacted, showPercent: true, color: calculateColor(contacted) }),
                React.createElement(Typography, { variant: 'subtitle2' }, t('common.contacted'))),
            data.responseOptions.length > 0 && (React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    gap: 2,
                    backgroundColor: themeColors.surface.secondary,
                } },
                React.createElement(CircleBar, { size: 64, strokeWidth: 5, antiClockwise: true, value: responded, showPercent: true, color: calculateColor(responded) }),
                React.createElement(Typography, { variant: 'subtitle2' }, t('common.responded'))))),
        React.createElement(AlertRespondersTable, { data: data, result: result })));
};
const calculateColor = (percent) => {
    if (percent >= 0.75) {
        return themeColors.accent.green.base;
    }
    else if (percent >= 0.25) {
        return themeColors.accent.orange.base;
    }
    else {
        return themeColors.accent.red.base;
    }
};
